import React, { useState } from 'react';
import './FAQ.css';

const FAQ_DATA = [
  {
    category: 'Getting Started',
    items: [
      {
        q: 'How do I create an account?',
        a: 'Click "Get Started" on the home page and fill in your name, email and a password. You\'ll land on your dashboard straight away.'
      },
      {
        q: 'How do I connect my business WhatsApp number?',
        a: 'Open Settings from the menu on the left and enter the two details Meta gives you for your WhatsApp Business account: an access token and a phone number ID.\nIf you don\'t have them to hand, whoever set up your Meta Business account can find them for you. Once saved, a green "WA Connected" badge appears in the sidebar — that\'s how you know you\'re live.'
      },
      {
        q: 'What do I need before I can send my first message?',
        a: 'Three things:\n1. Your TuWay Africa account (you\'re here, so that\'s done)\n2. Your business WhatsApp number connected in Settings\n3. At least one customer on your contact list'
      },
      {
        q: 'Can I use this on my phone?',
        a: 'Yes. Open TuWay Africa in your phone\'s browser and choose "Add to Home Screen". It then behaves like any other app on your phone, and it will hold onto anything you send while you\'re out of signal.'
      }
    ]
  },
  {
    category: 'Customers',
    items: [
      {
        q: 'How do I add a customer?',
        a: 'Go to Contacts and click "Add Contact". You need a name and a phone number that starts with the country code (for example +263771234567). An email address is optional.'
      },
      {
        q: 'Can I bring in the list I already have?',
        a: 'Yes. On the Contacts page click "Import CSV" and choose your spreadsheet — most spreadsheet programs can save one with "Save as → CSV". As long as it has columns for name, phone and email, we\'ll match them up and show you a preview before anything is added.'
      },
      {
        q: 'What does "opted in" and "opted out" mean?',
        a: 'Opted in means the customer is happy to hear from you. Opted out means they\'ve asked you to stop, and we will not include them in anything you send — that\'s what keeps your business on the right side of messaging rules. You can change either setting on a customer\'s profile.'
      },
      {
        q: 'How do tags work?',
        a: 'Tags are labels you put on customers so you can talk to the right group — "VIP", "New lead", "Harare". Add them when you create or edit a customer, then send a message to just that group instead of everyone.'
      },
      {
        q: 'Can I import contacts directly from my phone?',
        a: 'Yes. Open Contacts and choose "Import from Phone". Select the contacts you want to bring in, review them, and optionally add one tag to the whole batch so they are easy to find later. You can also keep using CSV import for larger lists.'
      },
      {
        q: 'Can I keep notes about a customer?',
        a: 'Yes. Open or edit the customer in Contacts and use the notes field for useful context such as preferences, previous enquiries or follow-up details. Notes stay with that customer so your team has the same background.'
      }
    ]
  },
  {
    category: 'Inbox & Conversations',
    items: [
      {
        q: 'What can I do in the shared Inbox?',
        a: 'The Inbox brings customer WhatsApp conversations into one team view. You can search by customer, read the message history, reply, add an attachment, use an approved WhatsApp template, and see whether outgoing messages were sent, delivered, read or failed.'
      },
      {
        q: 'How are conversations organised?',
        a: 'Use the Open, Pending and Closed tabs to separate active work from conversations waiting for something or already finished. You can change a conversation\'s status from its details panel and search across the tabs by name, phone number or email.'
      },
      {
        q: 'Can a conversation be assigned to a team member?',
        a: 'Yes. Open the conversation and choose the person responsible for it. Assigned team members receive an Inbox alert, and new chats are offered only to people whose presence is set to Available.'
      },
      {
        q: 'What is an internal note?',
        a: 'An internal note is visible to your team but is never sent to the customer. Use the "Internal note" tab in the reply box to leave handover details, reminders or context for the next person who opens the conversation.'
      }
    ]
  },
  {
    category: 'Broadcasts',
    items: [
      {
        q: 'How do I send a message to my customers?',
        a: 'Go to Broadcasts and click "New Broadcast". Choose whether it goes out by WhatsApp or email, pick who should get it (everyone, or one tagged group), write your message or pick a saved one, and send.'
      },
      {
        q: 'Should I send by WhatsApp or by email?',
        a: 'WhatsApp lands in the same chat list your customers use with family and friends, so it gets read quickly — best for time-sensitive news. Email suits longer messages and anything customers may want to keep. You can use both; send whichever your audience actually reads.'
      },
      {
        q: 'Can I schedule a message for later?',
        a: 'Yes. On the New Broadcast page, set a date and time under "Schedule for later" and click Schedule. We\'ll send it at that moment even if you\'ve closed the app and gone home. If you\'d rather send right away, use "Save & Send Now".'
      },
      {
        q: 'What happens if my internet drops while I\'m sending?',
        a: 'Nothing is lost. Your message is held safely and goes out on its own as soon as you\'re back online.'
      },
      {
        q: 'What are templates?',
        a: 'Templates are messages you\'ve written once and saved — a promotion, an appointment reminder, a thank-you. Anyone on your team can send one, so the wording stays consistent and nobody starts from a blank page.'
      },
      {
        q: 'Can I personalise a broadcast?',
        a: 'Yes. WhatsApp templates can use values such as the customer\'s name, with a fallback for contacts whose details are missing. You can preview the message and send a test to your own phone or email before sending it to the full audience.'
      },
      {
        q: 'How can I see who received or missed a broadcast?',
        a: 'Open the broadcast to see recipient-level lists for sent, delivered, read, failed and pending messages. Failed deliveries include the reason where one is available, which helps you correct phone numbers, connection problems or template issues before retrying.'
      }
    ]
  },
  {
    category: 'Catalog & Quotations',
    items: [
      {
        q: 'What is the Catalog for?',
        a: 'The Catalog is your private product and stock list inside TuWay Africa. Add products with a SKU, description, category, unit, price and tax rate, then record quantities in one or more warehouses. You can search and filter the list, spot low stock, move stock between warehouses, or import products from CSV.'
      },
      {
        q: 'Does the Catalog publish products to a WhatsApp shop?',
        a: 'No. It is an internal catalog used to manage products, stock and quotations. It does not publish products to Meta\'s commerce catalog or create a public WhatsApp storefront.'
      },
      {
        q: 'How do quotations work?',
        a: 'Create a quotation in the app, receive a request from a customer on WhatsApp, or create one through the API. Add products and quantities, review pricing, tax and validity, then generate a PDF and send it to the customer on WhatsApp.'
      },
      {
        q: 'Can I track whether a quotation was accepted?',
        a: 'Yes. Quotations move through clear stages: Needs pricing, Ready to send, Sent, Accepted, Declined or Expired. The Quotations page shows counts and values by stage, and lets you search by quote number or filter by status.'
      }
    ]
  },
  {
    category: 'Your Results',
    items: [
      {
        q: 'What do the dashboard numbers mean?',
        a: 'In plain terms:\n• Reached their phone — how many of your messages actually arrived\n• Read on WhatsApp — how many of those were opened\n• Clicked your link — how many customers tapped a link you shared\n• New customers — how much your list has grown in the past 30 days'
      },
      {
        q: 'How do I find out if anyone clicked?',
        a: 'Go to Link Tracker and paste in the web address you want to share. You get a short link back — use that in your message instead of the original. Every tap is counted, so you can see how much interest a message really created.'
      },
      {
        q: 'Where do I see how a particular message performed?',
        a: 'The Analytics page breaks each message down: how many went out, how many arrived, how many were read, and how many people clicked. It\'s the quickest way to see which offers and wording are worth repeating.'
      }
    ]
  },
  {
    category: 'Automatic Replies',
    items: [
      {
        q: 'Can the system reply without me?',
        a: 'Yes. You can set up rules that send a message on their own — a welcome note when someone new joins your list, or an answer whenever a customer asks about prices or opening hours. Customers get a fast reply even when your team is closed.'
      },
      {
        q: 'How do I set one up?',
        a: 'Go to Flow Triggers, click "Add Trigger", choose what should set it off (a new customer, or a word a customer sends you), write the reply, and save. From then on it runs by itself.'
      },
      {
        q: 'What is the difference between Flow Triggers and Automations?',
        a: 'Flow Triggers launch a Meta WhatsApp Flow, such as a registration or data-capture form. Automations build a multi-step conversation on a visual canvas. Use a Flow when the customer should complete a structured form, and an Automation when you want to guide them through messages, questions and choices.'
      },
      {
        q: 'What can I add to an Automation?',
        a: 'An Automation starts when a customer sends one of your chosen keywords. On the canvas you can add messages, questions, reply buttons, tag actions and WhatsApp templates, then connect the steps into the route the customer should follow. You can pause an Automation while editing and activate it when it is ready.'
      },
      {
        q: 'Can I tell whether an Automation is being used?',
        a: 'Yes. Each Automation shows how many customer journeys started and how many completed. Its card also shows the trigger keywords and whether it is Live or Paused.'
      }
    ]
  },
  {
    category: 'Workforce & Team',
    items: [
      {
        q: 'What does Workforce Management show?',
        a: 'Workforce Management gives the team a shared view of presence, expected workload, schedules, efficiency, login hours, service levels and alerts. The exact tabs and actions you can use depend on the access your account owner has given you.'
      },
      {
        q: 'Can we plan shifts and forecast busy periods?',
        a: 'Yes. Use Forecast to review expected conversation demand and Schedule to view planned shifts. Team owners can create and publish schedules, while team members can see the shifts relevant to their work.'
      },
      {
        q: 'What performance information is available for the team?',
        a: 'The Efficiency, Login Hours and SLA tabs show how long people were logged in, time spent active or in other statuses, response performance, and how much incoming work was handled. Date and time filters let you examine the period you care about.'
      },
      {
        q: 'What are workforce alerts?',
        a: 'Alerts draw attention to conditions that need follow-up, such as long breaks or response times outside the limits set for the workspace. Team owners choose the thresholds; the alert view helps supervisors act before service slips.'
      }
    ]
  },
  {
    category: 'Account & Settings',
    items: [
      {
        q: 'How do I switch between dark and light mode?',
        a: 'Click the moon or sun icon at the bottom of the menu on the left. We\'ll remember your choice next time you sign in.'
      },
      {
        q: 'How do I update my WhatsApp connection details?',
        a: 'Go to Settings, enter the new access token and phone number ID from your Meta Business account, and save. The sidebar tells you straight away whether the connection is working.'
      },
      {
        q: 'Can my team share one account?',
        a: 'Everyone should sign in as themselves — that\'s what lets you see who replied to which customer and how your team is spending its day. Add your team from the Team section of Settings, and choose what each person is allowed to see.'
      },
      {
        q: 'How do the agent statuses work?',
        a: 'Each person on your team sets their own status from the bottom of the menu: Available when they\'re taking customer chats, Break for a tea break or lunch, Not Ready when they\'re in a meeting or on other work, and Offline when they finish for the day. New chats only go to people who are Available.\nBreak time comes out of a daily allowance you set yourself under Workforce Management → Presence, so nobody has to police it by hand.'
      },
      {
        q: 'I forgot my password. What do I do?',
        a: 'On the sign-in page click "Forgot password?" and enter your email. We\'ll send you a link that works for one hour — open it, choose a new password, and you\'re back in.'
      }
    ]
  },
  {
    category: 'If Something Looks Wrong',
    items: [
      {
        q: 'My messages aren\'t reaching customers. What should I check?',
        a: 'Work down this list — it\'s almost always one of these:\n1. The sidebar shows "WA Connected" in green (if not, see Settings)\n2. The customer\'s number starts with the country code, e.g. +263771234567\n3. The customer hasn\'t opted out\n4. Your WhatsApp access token from Meta hasn\'t expired — these do run out and need replacing\nIf all four look right, your administrator can check the delivery log for the exact reason Meta gave.'
      },
      {
        q: 'The sidebar says "WA Disconnected". How do I fix it?',
        a: 'Go to Settings and re-enter your WhatsApp access token and phone number ID. If it still won\'t connect, the details may have changed or expired on Meta\'s side — whoever manages your Meta Business account can confirm the account is active and issue fresh ones.'
      },
      {
        q: 'It says I\'m offline but my internet is fine.',
        a: 'Refresh the page. Browsers sometimes get this wrong. If the message stays, sign out and back in, or try a different browser.'
      },
      {
        q: 'My spreadsheet import isn\'t working.',
        a: 'Save the spreadsheet as CSV with a header row containing "phone" or "email" (plus optional "name" and "country_code"). Local numbers without a country code are treated as Zimbabwean and corrected when valid. Check the preview: rows with invalid phone numbers are skipped and listed in the errors, while valid rows continue importing.'
      }
    ]
  }
];

const CATEGORIES = ['All', ...FAQ_DATA.map(s => s.category)];

export default function FAQ() {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [openItems, setOpenItems] = useState({});

  const toggle = (key) => {
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const filtered = FAQ_DATA
    .filter(section => activeCategory === 'All' || section.category === activeCategory)
    .map(section => ({
      ...section,
      items: section.items.filter(item => {
        if (!search) return true;
        const s = search.toLowerCase();
        return item.q.toLowerCase().includes(s) || item.a.toLowerCase().includes(s);
      })
    }))
    .filter(section => section.items.length > 0);

  const totalResults = filtered.reduce((sum, s) => sum + s.items.length, 0);

  return (
    <div className="faq-page">
      <h1>Help & FAQ</h1>
      <p className="faq-subtitle">Straight answers to the questions people ask most</p>

      <div className="faq-search">
        <span className="faq-search-icon">&#128269;</span>
        <input
          type="text"
          placeholder="What do you need help with?"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      <div className="faq-categories">
        {CATEGORIES.map(cat => (
          <button
            key={cat}
            className={'faq-cat-btn' + (activeCategory === cat ? ' active' : '')}
            onClick={() => setActiveCategory(cat)}
          >
            {cat}
          </button>
        ))}
      </div>

      {totalResults === 0 ? (
        <div className="faq-no-results">
          <span>&#128533;</span>
          <p>No results found for "{search}"</p>
          <p>Try different keywords or browse by category</p>
        </div>
      ) : (
        filtered.map(section => (
          <div key={section.category}>
            <h2 className="faq-section-title">{section.category}</h2>
            {section.items.map((item, i) => {
              const key = section.category + '-' + i;
              const isOpen = openItems[key];
              return (
                <div className="faq-item" key={key}>
                  <button className="faq-question" onClick={() => toggle(key)}>
                    {item.q}
                    <span className={'faq-chevron' + (isOpen ? ' open' : '')}>&#9660;</span>
                  </button>
                  {isOpen && (
                    <div className="faq-answer">
                      {item.a.split('\n').map((line, j) => (
                        <p key={j}>{line}</p>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))
      )}

      <div className="faq-contact">
        <h3>Still need help?</h3>
        <p>Ask your administrator — they can see the delivery logs and account settings behind the scenes.</p>
      </div>
    </div>
  );
}
